import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  makeStyles,
  createStyles,
} from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import EditorJS from '@editorjs/editorjs';

import { selectAutoSave } from '../../store/modules/scenes/selectors';
import { CheckCircleIcon } from '../icon/solid';
import { Tooltip } from '../index';
import { parseJsonToEditorState } from '../../services/editorJS/utils';
import {
  saveScenesRequest,
  setEditingScript,
  setSavedScript,
  setSavingScript,
} from '../../store/modules/scenes/actions';
import CloseBrowserTabDetector from '../../services/common/closeBrowserTabDetector';

/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps */

export const useStyles = makeStyles(() => createStyles({
  root: {
    color: '#00AD8D',
    fontSize: 16,
    fontWeight: 'bold',
    height: '100%',
    cursor: 'pointer',
  },
}), { name: 'ScriptEditorAutoSave' });

type Props = {
  instanceEditor: EditorJS | undefined;
  name: string;
};

let isChanged = false;

const AutoSaveComponent = ({ instanceEditor, name }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getValues } = useFormContext();
  const [formData] = useState(getValues());
  const { editing, saving, saved } = useSelector(selectAutoSave);

  useEffect(() => {
    if (!saved !== isChanged) {
      isChanged = !saved;
    }
  }, [saved, isChanged]);

  useEffect(() => {
    const closeBrowserTabDetector = new CloseBrowserTabDetector({});
    if (!saved) {
      closeBrowserTabDetector.init();
    }

    return () => {
      if (!saved) {
        closeBrowserTabDetector.destroy();
      }
    };
  }, [saved]);

  useEffect(() => () => {
    /**
     * Auto save after unmount if is changed
     * */
    if (instanceEditor && isChanged) {
      instanceEditor.save().then((outputData) => {
        const fieldData = parseJsonToEditorState(getValues()[name]);
        const scriptData = { ...fieldData, ...outputData };
        formData[name] = JSON.stringify(scriptData);
        formData.notifyLastScriptAutoSave = true;
        dispatch(saveScenesRequest(formData as any));
      });
    }
  }, [dispatch, instanceEditor]);

  useEffect(() => {
    dispatch(setEditingScript(false));
    dispatch(setSavingScript(false));
    dispatch(setSavedScript(true));
  }, [dispatch]);

  return (
    <>
      {saved
        && (
          <Tooltip title="Changes saved">
            <CheckCircleIcon className={classes.root} />
          </Tooltip>
        )}
      {saving
        && (
          <span className={classes.root}>Saving...</span>
        )}
      {(editing && !saved && !saving)
        && (
          <Tooltip title="Un-saved changes">
            <span className={classes.root}>...</span>
          </Tooltip>
        )}
    </>
  );
};

export default AutoSaveComponent;
